import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import {
    putInvoiceFileToSignedUrl,
    signedUrlKindForFile,
    type InvoiceSignedUrlKind,
} from '../../utils/invoiceUploadFiles';

interface SignedUrlEntry {
    signedUrl: string;
    key: string;
}

interface GetSignedUrlsResponse {
    urls: SignedUrlEntry[];
}

const uploadInvoice = async (
    sessionId: string,
    payload: {
        imageFiles?: File[];
        pdfFile?: File;
        locationId?: string;
        invoiceType: 'received' | 'returned' | 'return_slip' | 'sublet';
        /** Optional; if omitted, RO will be extracted from the invoice image during processing. */
        roNumber?: string;
        /** Optional NEX credit-card vendor (received/sublet single uploads only). */
        ccVendorId?: number;
        ccVendorName?: string;
    }
): Promise<{ uuid: string }> => {
    const headers = { Authorization: sessionId, 'Content-Type': 'application/json' };
    const hasImages = payload.imageFiles != null && payload.imageFiles.length > 0;
    const hasPdf = payload.pdfFile != null;

    if (!hasImages && !hasPdf) {
        throw new Error('Please upload at least one image or a PDF.');
    }
    if (hasImages && hasPdf) {
        throw new Error('Upload either images or a PDF, not both.');
    }

    const files = hasPdf && payload.pdfFile ? [payload.pdfFile] : payload.imageFiles!;
    const sequence: InvoiceSignedUrlKind[] = [];
    for (const file of files) {
        const kind = signedUrlKindForFile(file);
        if (kind == null) {
            throw new Error('Only JPG, PNG, TIF, and PDF files are supported.');
        }
        sequence.push(kind);
    }

    const signedUrlsRes = await axios.post(
        `${API_URL}/invoice/get-signed-urls`,
        { sequence },
        { headers, validateStatus: () => true }
    );
    if (signedUrlsRes.status !== 200) {
        const err = signedUrlsRes.data?.error ?? 'Failed to get upload URLs';
        throw new Error(err);
    }
    const { urls } = signedUrlsRes.data as GetSignedUrlsResponse;
    if (!urls || urls.length !== files.length) {
        throw new Error('Unexpected number of upload URLs returned');
    }

    await Promise.all(
        files.map((file, i) => putInvoiceFileToSignedUrl(urls[i]!.signedUrl, file, sequence[i]!)),
    );

    const s3Keys = urls.map(u => u.key);
    const uploadRes = await axios.post(
        `${API_URL}/invoice/upload`,
        {
            s3Keys,
            ...(payload.locationId ? { locationId: payload.locationId } : {}),
            invoiceType: payload.invoiceType,
            ...(payload.roNumber != null && payload.roNumber !== '' ? { roNumber: payload.roNumber } : {}),
            ...((payload.invoiceType === 'received' || payload.invoiceType === 'sublet') &&
            payload.ccVendorId != null
                ? {
                    ccVendorId: payload.ccVendorId,
                    ...(payload.ccVendorName ? { ccVendorName: payload.ccVendorName } : {}),
                }
                : {}),
        },
        { headers, validateStatus: () => true }
    );

    if (uploadRes.status !== 200) {
        const body = uploadRes.data as { error?: string; details?: string; };
        const msg = body?.details ?? body?.error ?? 'Upload failed';
        throw new Error(msg);
    }

    return uploadRes.data as { uuid: string; };
};

export const useUploadInvoice = () => {
    const sessionContext = useContext(SessionContext);
    return useMutation({
        mutationFn: (variables: {
            imageFiles?: File[];
            pdfFile?: File;
            locationId?: string;
            invoiceType: 'received' | 'returned' | 'return_slip' | 'sublet';
            roNumber?: string;
            ccVendorId?: number;
            ccVendorName?: string;
        }) =>
            uploadInvoice(sessionContext?.sessionId ?? '', variables),
    });
};
